const VALIDATION_TYPE = {
  SINGLE_WORD: 'single_word',
  MULTIPLE_WORD: 'multiple_word',
};

const parseCommaSeparatedList = (value) =>
  (value || '')
    .split(/[\n,]+/)
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);

const parsePersonalisationConfig = (element) => {
  const configNode = element.querySelector('[data-custom-text-config]');

  if (!configNode) return null;

  try {
    return JSON.parse(configNode.textContent);
  } catch {
    return null;
  }
};

class ProductCustomTextPersonalisation extends HTMLElement {
  connectedCallback() {
    this.productForm = this.closest('form');
    this.textInput = this.querySelector('[data-custom-text-input]');
    this.charCounter = this.querySelector('[data-custom-text-counter]');
    this.confirmationCheckbox = this.querySelector('[data-custom-text-confirm]');
    this.errorMessage = this.querySelector('[data-custom-text-error]');
    this.confirmationErrorMessage = this.querySelector('[data-custom-text-confirm-error]');
    this.addToCartButton = this.productForm ? this.productForm.querySelector('[data-add-to-cart]') : null;
    this.variantButtonContainer = this.productForm ? this.productForm.querySelector('.js-product-cta-buttons') : null;
    this.variantIdInput = this.productForm ? this.productForm.querySelector('[name="id"]') : null;
    const config = parsePersonalisationConfig(this);

    if (!config || !this.productForm || !this.textInput || !this.charCounter || !this.confirmationCheckbox || !this.addToCartButton) {
      return;
    }

    this.minLength = parseInt(config.minLength || '1', 10);
    this.maxLength = parseInt(config.maxLength || '12', 10);
    this.propertyInputName = this.textInput.getAttribute('name');
    this.restrictedWords = parseCommaSeparatedList(config.restrictedWords);
    this.singleWordSkus = parseCommaSeparatedList(config.singleWordSkus);
    this.multipleWordSkus = parseCommaSeparatedList(config.multipleWordSkus);
    this.variantSkuMap = Array.isArray(config.variantSkus) ? config.variantSkus : [];
    this.currentVariantSku = (config.currentVariantSku || '').trim().toUpperCase();
    this.errors = {
      length: config.errors?.length || 'Enter up to 12 characters.',
      invalidSingle: config.errors?.invalidSingle || 'Please use Title Case (first letter uppercase) or UPPERCASE (all letters uppercase). Letters and numbers only.',
      invalidMultiple: config.errors?.invalidMultiple || 'Please use UPPERCASE letters, numbers, spaces and & only.',
      confirm: config.errors?.confirm || 'Please confirm your personalisation.',
      restricted: config.errors?.restricted || 'Please choose different text.',
    };
    this.hasSubmitAttempt = false;

    this.handleTextInput = () => this.cleanTextAndUpdateCounter();
    this.handleTextFocus = () => {
      if (!this.hasSubmitAttempt) {
        this.showTextError('');
      }
    };
    this.handleTextBlur = () =>
      this.validatePersonalisation({
        showErrors: true,
        skipConfirmationError: true,
      });
    this.handleConfirmChange = () => this.validatePersonalisation({ showErrors: this.hasSubmitAttempt });
    this.handleAddToCartClick = () => this.showValidationErrors();
    this.handleSubmitAttempt = (event) => this.onSubmitAttempt(event);
    this.handleFormChange = (event) => {
      if (event.target === this.textInput || event.target === this.confirmationCheckbox) {
        return;
      }

      window.requestAnimationFrame(() => {
        this.updateAutocapitalize();
        this.cleanTextAndUpdateCounter();
        this.validatePersonalisation({ showErrors: this.hasSubmitAttempt });
      });
    };

    this.textInput.addEventListener('input', this.handleTextInput);
    this.textInput.addEventListener('focus', this.handleTextFocus);
    this.textInput.addEventListener('blur', this.handleTextBlur);
    this.confirmationCheckbox.addEventListener('change', this.handleConfirmChange);
    this.addToCartButton.addEventListener('click', this.handleAddToCartClick);
    this.productForm.addEventListener('submit', this.handleSubmitAttempt, true);
    this.productForm.addEventListener('change', this.handleFormChange);

    this.updateAutocapitalize();
    this.cleanTextAndUpdateCounter();
  }

  disconnectedCallback() {
    if (!this.productForm || !this.textInput) return;

    this.textInput.removeEventListener('input', this.handleTextInput);
    this.textInput.removeEventListener('focus', this.handleTextFocus);
    this.textInput.removeEventListener('blur', this.handleTextBlur);
    this.confirmationCheckbox.removeEventListener('change', this.handleConfirmChange);
    this.addToCartButton.removeEventListener('click', this.handleAddToCartClick);
    this.productForm.removeEventListener('submit', this.handleSubmitAttempt, true);
    this.productForm.removeEventListener('change', this.handleFormChange);
  }

  getSelectedVariantSku() {
    if (!this.variantIdInput) return this.currentVariantSku;

    const variantId = this.variantIdInput.value;
    const variantEntry = this.variantSkuMap.find((entry) => String(entry.id) === String(variantId));

    return (variantEntry?.sku || this.currentVariantSku).trim().toUpperCase();
  }

  getValidationTypeForSku(sku) {
    if (sku && this.multipleWordSkus.includes(sku)) {
      return VALIDATION_TYPE.MULTIPLE_WORD;
    }

    return VALIDATION_TYPE.SINGLE_WORD;
  }

  containsRestrictedWord(value) {
    const upperValue = value.toUpperCase();
    return this.restrictedWords.some((word) => upperValue.includes(word));
  }

  selectedVariantCanBeAdded() {
    return (
      !this.productForm.classList.contains('c-product-form--variant-sold-out') &&
      (!this.variantButtonContainer || !this.variantButtonContainer.classList.contains('is-hidden'))
    );
  }

  showTextError(message) {
    if (!this.errorMessage) return;

    if (message) {
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
      this.textInput.setAttribute('aria-invalid', 'true');
      return;
    }

    this.errorMessage.textContent = '';
    this.errorMessage.hidden = true;
    this.textInput.removeAttribute('aria-invalid');
  }

  showConfirmationError(message) {
    if (!this.confirmationErrorMessage) return;

    if (message) {
      this.confirmationErrorMessage.textContent = message;
      this.confirmationErrorMessage.hidden = false;
      this.confirmationCheckbox.setAttribute('aria-invalid', 'true');
      return;
    }

    this.confirmationErrorMessage.textContent = '';
    this.confirmationErrorMessage.hidden = true;
    this.confirmationCheckbox.removeAttribute('aria-invalid');
  }

  getTrimmedText() {
    return this.textInput.value.trim();
  }

  getSelectedValidationType() {
    return this.getValidationTypeForSku(this.getSelectedVariantSku());
  }

  getCountedText(textValue, validationType) {
    if (validationType === VALIDATION_TYPE.MULTIPLE_WORD) {
      return textValue.replace(/ /g, '');
    }

    return textValue;
  }

  normalizeTextValue(value, validationType) {
    if (validationType === VALIDATION_TYPE.MULTIPLE_WORD) {
      return value.replace(/[a-z]/g, (letter) => letter.toUpperCase());
    }

    return value;
  }

  updateAutocapitalize() {
    const validationType = this.getSelectedValidationType();

    this.textInput.setAttribute(
      'autocapitalize',
      validationType === VALIDATION_TYPE.MULTIPLE_WORD ? 'characters' : 'off'
    );
  }

  hasValidSingleWordCase(textValue) {
    const letters = textValue.match(/[A-Za-z]/g) || [];

    if (letters.length === 0) return true;

    const isUppercase = letters.every((letter) => letter === letter.toUpperCase());
    const firstLetterIndex = textValue.search(/[A-Za-z]/);
    const isTitleCase =
      letters.every((letter, index) => {
        if (index === 0) return letter === letter.toUpperCase();

        return letter === letter.toLowerCase();
      }) && firstLetterIndex >= 0;

    return isUppercase || isTitleCase;
  }

  getTextValidationState() {
    const textValue = this.getTrimmedText();
    const validationType = this.getSelectedValidationType();
    const countedText = this.getCountedText(textValue, validationType);
    const textLength = countedText.length;
    const isLongEnough = textLength >= this.minLength;
    const isShortEnough = textLength <= this.maxLength;
    const hasAllowedCharacters = validationType === VALIDATION_TYPE.MULTIPLE_WORD
      ? /^[A-Za-z0-9 &]+$/.test(textValue)
      : /^[A-Za-z0-9]+$/.test(textValue);
    const hasValidCase = validationType === VALIDATION_TYPE.MULTIPLE_WORD
      ? /^[A-Z0-9 &]+$/.test(textValue)
      : this.hasValidSingleWordCase(textValue);
    const hasBlockedWord = this.containsRestrictedWord(textValue);
    const isValid =
      isLongEnough &&
      isShortEnough &&
      hasAllowedCharacters &&
      hasValidCase &&
      !hasBlockedWord;

    if (hasBlockedWord) {
      return {
        isValid,
        errorMessage: this.errors.restricted,
      };
    }

    if (!isLongEnough || !isShortEnough) {
      return {
        isValid,
        errorMessage: this.errors.length,
      };
    }

    if (!hasAllowedCharacters || !hasValidCase) {
      return {
        isValid,
        errorMessage: validationType === VALIDATION_TYPE.MULTIPLE_WORD
          ? this.errors.invalidMultiple
          : this.errors.invalidSingle,
      };
    }

    return { isValid, errorMessage: '' };
  }

  preparePersonalisationForSubmit() {
    if (this.propertyInputName) {
      this.textInput.setAttribute('name', this.propertyInputName);
    }

    const validationType = this.getSelectedValidationType();
    this.textInput.value = this.normalizeTextValue(this.getTrimmedText(), validationType);
  }

  validatePersonalisation({ showErrors = false, skipConfirmationError = false } = {}) {
    this.addToCartButton.disabled = !this.selectedVariantCanBeAdded();

    const validationState = this.getTextValidationState();

    if (showErrors) {
      this.showTextError(validationState.errorMessage);
    } else if (validationState.isValid) {
      this.showTextError('');
    }

    if (this.confirmationCheckbox.checked) {
      this.showConfirmationError('');
    } else if (showErrors && validationState.isValid && !skipConfirmationError) {
      this.showConfirmationError(this.errors.confirm);
    } else if (!showErrors) {
      this.showConfirmationError('');
    }
  }

  cleanTextAndUpdateCounter() {
    const selectionStart = this.textInput.selectionStart;
    const selectionEnd = this.textInput.selectionEnd;
    const validationType = this.getSelectedValidationType();
    const normalizedValue = this.normalizeTextValue(this.textInput.value, validationType);

    if (this.textInput.value !== normalizedValue) {
      this.textInput.value = normalizedValue;
    }

    const countedText = this.getCountedText(this.getTrimmedText(), validationType);

    if (selectionStart !== null && selectionEnd !== null && document.activeElement === this.textInput) {
      this.textInput.setSelectionRange(selectionStart, selectionEnd);
    }

    this.charCounter.textContent = `${countedText.length}/${this.maxLength}`;
    this.validatePersonalisation({ showErrors: false });
  }

  applySubmitValidation({ blockSubmit = false, event = null } = {}) {
    this.hasSubmitAttempt = true;
    this.preparePersonalisationForSubmit();

    const validationState = this.getTextValidationState();

    if (!validationState.isValid) {
      this.showTextError(validationState.errorMessage);
      this.showConfirmationError('');

      if (blockSubmit && event) {
        this.blockInvalidSubmit(event);
        this.textInput.focus();
      }

      return false;
    }

    this.showTextError('');

    if (!this.confirmationCheckbox.checked) {
      this.showConfirmationError(this.errors.confirm);

      if (blockSubmit && event) {
        this.blockInvalidSubmit(event);
        this.confirmationCheckbox.focus();
      }

      return false;
    }

    this.showConfirmationError('');
    return true;
  }

  showValidationErrors() {
    this.applySubmitValidation();
  }

  blockInvalidSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  onSubmitAttempt(event) {
    this.applySubmitValidation({ blockSubmit: true, event });
  }
}

if (!customElements.get('product-custom-text-personalisation')) {
  customElements.define('product-custom-text-personalisation', ProductCustomTextPersonalisation);
}
